import React, { useEffect, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Stack, Typography } from '@mui/material';
import * as changesets from 'json-diff-ts';
import { useForm } from 'react-hook-form';

import ControlledTextField from '../../../../../RiverusUI/Components/ControlledTextField';
import RadioButtonGroup from '../../../../../RiverusUI/Components/RadioButtonGroup';
import { ClauseType } from '../../../../Constants/ClauseType';
import {
  editedPhrases,
  LinkPhraseRequest,
  phraseInfo,
  SentencesData,
  tableInfo,
} from '../../../../State/documentState';
import {
  getClauseDataFormat,
  getClauseObjects,
} from '../../../ClauseComponent/utils/ClauseTypeUtils';
import {
  filterPhrasesFromPhrasesArray,
  filterTableCellsFromPhraseArray,
  isTableCell,
  phraseBiMap,
} from '../../../Utils/docUtils';

interface Props {
  dataPointName: string;
  saveHighlightedDataPoint: (dataPointName: string) => void;
  editOptionSelected: (editOptionSelected: boolean) => void;
  savedInsight: any;
  savedHighlightedPhrases: phraseInfo[] | null;
  saveHighlightedPhrases: (
    savedHighlightedPhrases: phraseInfo[] | null
  ) => void;
  phraseEditOptionSelected: boolean;
  savePhraseEditOption: (phraseEditOptionSelected: boolean) => void;
  phraseInDeleteMode: phraseInfo | null;
  phraseDeleteStatus: boolean;
  saveDeletePhrase: (
    phraseInDeleteMode: phraseInfo | null,
    phraseDeleteStatus: boolean
  ) => void;
  phraseInAddMode: phraseInfo | null;
  phraseAddStatus: boolean;
  saveAddPhrase: (
    phraseInAddMode: phraseInfo | null,
    phraseAddStatus: boolean
  ) => void;
  savedHighlightedTableCells: tableInfo[] | null;
  saveHighlightedTableCells: (
    savedHighlightedTableCells: tableInfo[] | null
  ) => void;
  postClauseDataByType: (
    fileID: string,
    type: ClauseType,
    payload: any,
    updatedObject: any
  ) => void;
  updatedClauseData: any;
  fileId: string;
  clauseType: string;
  sentenceData: SentencesData;
  clauseDataByType: any;
  onClose: VoidFunction;
  updatedClauseDataByType: any;
  clauseData: any;
}

const EditAddDefinition: React.FC<Props> = (props) => {
  const [isAddingPhrase, setIsAddingPhrase] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEditingState, setIsEditingState] = useState<boolean>(false);
  const [currentEditingPhrase, setCurrentEditingPhrase] =
    useState<phraseInfo | null>(null);
  const [definitionTitleText, setDefinitionTitleText] = useState<string>('');

  const clauseName = props?.dataPointName?.replace(/_/g, ' ');
  const { control, watch } = useForm({
    defaultValues: {
      isPresent: 'Yes',
      definitionTitle: '',
    },
  });

  const clauseOptions = [
    {
      value: 'Yes',
      title: `${clauseName} clause is present`,
    },
    {
      value: 'No',
      title: `${clauseName}  clause is not present`,
    },
  ];

  const isPresent = watch('isPresent') || '';

  useEffect(() => {
    props.saveHighlightedDataPoint(props.dataPointName);
  }, []);

  useEffect(() => {
    if (props.phraseInAddMode !== null) {
      setIsAddingPhrase(false);
    }
  }, [props.phraseInAddMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDefinitionTitleText(e.target.value);
  };

  const linkToPhrase = () => {
    props.savePhraseEditOption(true);
    setIsAddingPhrase(true);
    setCurrentEditingPhrase(null);
    setIsEditingState(false);
    props.saveDeletePhrase(null, false);
    props.saveHighlightedTableCells(null);
    props.editOptionSelected(true);
    props.saveHighlightedDataPoint(props.dataPointName);
  };

  const editLinkedPhraseOnDoc = (phraseInfo: phraseInfo) => {
    let deletePhraseElement = '';
    if (isTableCell(phraseInfo)) {
      const tempTableCell: tableInfo = {
        paraId: phraseInfo.paraId,
        rowId: phraseInfo.rowId !== null ? phraseInfo.rowId : -1,
        columnId: phraseInfo.columnId !== null ? phraseInfo.columnId : -1,
      };
      deletePhraseElement =
        'p' +
        phraseInfo.paraId +
        ';r' +
        phraseInfo.rowId +
        ';c' +
        phraseInfo.columnId;
      props.saveHighlightedTableCells([tempTableCell]);
    } else {
      deletePhraseElement =
        'p' + phraseInfo.paraId + ';w' + phraseInfo.startWordId;
      props.saveHighlightedTableCells(null);
    }
    const phraseElement = document.getElementById(deletePhraseElement);
    if (phraseElement) {
      phraseElement.scrollIntoView({ block: 'center' });
      document.documentElement.style.scrollBehavior = 'smooth';
    }
    props.saveDeletePhrase(phraseInfo, true);
    props.saveHighlightedDataPoint(props.dataPointName);
    setIsEditingState(true);
    setIsAddingPhrase(false);
    setCurrentEditingPhrase(phraseInfo);
    props.editOptionSelected(true);
  };
  const linkPhraseOnDoc = (phraseInfo: phraseInfo | null) => {
    if (phraseInfo === null) {
      return (
        <Stack width="100%" alignItems="start">
          <Button
            sx={{
              color:
                isAddingPhrase !== true
                  ? '#88305F'
                  : props.phraseEditOptionSelected === true
                    ? '#C1C1C1'
                    : '#88305F',
              padding: 0,
            }}
            onClick={() => linkToPhrase()}
          >
            Link phrase
          </Button>
        </Stack>
      );
    } else {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="start"
        >
          <Button
            sx={{
              color:
                currentEditingPhrase === phraseInfo ? '#C1C1C1' : '#88305F',
              padding: 0,
            }}
            onClick={() => editLinkedPhraseOnDoc(phraseInfo)}
          >
            Edit linked phrase
          </Button>
          <Typography fontSize="14px">{phraseInfo === null ? 0 : 1}</Typography>
        </Stack>
      );
    }
  };

  const onCancel = () => {
    props.onClose();
    props.saveDeletePhrase(null, false);
    props.saveAddPhrase(null, false);
    props.saveHighlightedPhrases(null);
    props.savePhraseEditOption(false);
    props.editOptionSelected(false);
    props.saveHighlightedTableCells(null);
  };

  const getAddedAndDeletedPhrases = (
    previousLinkedPhrases: phraseInfo[],
    changedLinkedPhrases: phraseInfo[]
  ): editedPhrases => {
    const addedPhrases: phraseInfo[] = [];
    const deletedPhrases: phraseInfo[] = [];
    if (previousLinkedPhrases.length !== 0) {
      if (changedLinkedPhrases !== null && changedLinkedPhrases.length !== 0) {
        //get newly added phrases
        for (let i = 0; i < changedLinkedPhrases.length; i++) {
          let exists = false;
          for (let j = 0; j < previousLinkedPhrases.length; j++) {
            if (
              changedLinkedPhrases[i].paraId === previousLinkedPhrases[j].paraId
            ) {
              if (
                changedLinkedPhrases[i].startWordId ===
                  previousLinkedPhrases[j].startWordId &&
                changedLinkedPhrases[i].endWordId ===
                  previousLinkedPhrases[j].endWordId
              ) {
                exists = true;
                break;
              }
            }
          }
          if (exists === false) {
            addedPhrases.push({
              paraId: changedLinkedPhrases[i].paraId,
              startWordId: changedLinkedPhrases[i].startWordId,
              endWordId: changedLinkedPhrases[i].endWordId,
              startSentenceId: changedLinkedPhrases[i].startSentenceId,
              endSentenceId: changedLinkedPhrases[i].endSentenceId,
              rowId: changedLinkedPhrases[i].rowId,
              columnId: changedLinkedPhrases[i].columnId,
              phrase: changedLinkedPhrases[i].phrase,
              definedword: definitionTitleText,
            });
          }
        }

        //get Deleted phrases
        for (let i = 0; i < previousLinkedPhrases.length; i++) {
          let exists = false;
          for (let j = 0; j < changedLinkedPhrases.length; j++) {
            if (
              previousLinkedPhrases[i].paraId === changedLinkedPhrases[j].paraId
            ) {
              if (
                previousLinkedPhrases[i].startWordId ===
                  changedLinkedPhrases[j].startWordId &&
                previousLinkedPhrases[i].endWordId ===
                  changedLinkedPhrases[j].endWordId
              ) {
                exists = true;
                break;
              }
            }
          }
          if (exists === false) {
            deletedPhrases.push({
              paraId: previousLinkedPhrases[i].paraId,
              startWordId: previousLinkedPhrases[i].startWordId,
              endWordId: previousLinkedPhrases[i].endWordId,
              startSentenceId: previousLinkedPhrases[i].startSentenceId,
              endSentenceId: previousLinkedPhrases[i].endSentenceId,
              rowId: previousLinkedPhrases[i].rowId,
              columnId: previousLinkedPhrases[i].columnId,
              phrase: previousLinkedPhrases[i].phrase,
            });
          }
        }
        //all deleted
      } else if (changedLinkedPhrases.length === 0) {
        for (let i = 0; i < previousLinkedPhrases.length; i++) {
          deletedPhrases.push(previousLinkedPhrases[i]);
        }
      }
    } else {
      //newly added
      if (changedLinkedPhrases !== null && changedLinkedPhrases.length !== 0) {
        for (let i = 0; i < changedLinkedPhrases.length; i++) {
          addedPhrases.push({
            paraId: changedLinkedPhrases[i].paraId,
            startWordId: changedLinkedPhrases[i].startWordId,
            endWordId: changedLinkedPhrases[i].endWordId,
            startSentenceId: changedLinkedPhrases[i].startSentenceId,
            endSentenceId: changedLinkedPhrases[i].endSentenceId,
            rowId: changedLinkedPhrases[i].rowId,
            columnId: changedLinkedPhrases[i].columnId,
            phrase: changedLinkedPhrases[i].phrase,
            definedword: definitionTitleText,
          });
        }
      }
    }
    const biType = phraseBiMap[props.dataPointName];

    const tempEditedPhrases: editedPhrases = {
      upsert: addedPhrases,
      deleted: deletedPhrases,
      bi: biType,
    };
    return tempEditedPhrases;
  };

  const getEditedTableCellPhrases = (
    previousLinkedTableCells: phraseInfo[],
    changedLinkedTableCells: phraseInfo[]
  ) => {
    const addedTableCells: phraseInfo[] = [];
    const deletedTableCells: phraseInfo[] = [];
    if (previousLinkedTableCells.length > 0) {
      if (changedLinkedTableCells.length > 0) {
        //newly added
        for (let i = 0; i < changedLinkedTableCells.length; i++) {
          let addedCellExists = false;
          for (let j = 0; j < previousLinkedTableCells.length; j++) {
            if (
              changedLinkedTableCells[i].paraId ===
                previousLinkedTableCells[j].paraId &&
              changedLinkedTableCells[i].rowId ===
                previousLinkedTableCells[j].rowId &&
              changedLinkedTableCells[i].columnId ===
                previousLinkedTableCells[j].columnId
            ) {
              addedCellExists = true;
              break;
            }
          }
          if (addedCellExists === false) {
            addedTableCells.push({
              paraId: changedLinkedTableCells[i].paraId,
              startSentenceId: changedLinkedTableCells[i].startSentenceId,
              endSentenceId: changedLinkedTableCells[i].endSentenceId,
              startWordId: changedLinkedTableCells[i].startWordId,
              endWordId: changedLinkedTableCells[i].endWordId,
              rowId: changedLinkedTableCells[i].rowId,
              columnId: changedLinkedTableCells[i].columnId,
              phrase: changedLinkedTableCells[i].phrase,
              definedword: definitionTitleText,
            });
          }
        }

        //deleted elements
        for (let i = 0; i < previousLinkedTableCells.length; i++) {
          let deletedCellExists = false;
          for (let j = 0; j < changedLinkedTableCells.length; j++) {
            if (
              previousLinkedTableCells[i].paraId ===
                changedLinkedTableCells[j].paraId &&
              previousLinkedTableCells[i].rowId ===
                changedLinkedTableCells[j].rowId &&
              previousLinkedTableCells[i].columnId ===
                changedLinkedTableCells[j].columnId
            ) {
              deletedCellExists = true;
              break;
            }
          }
          if (deletedCellExists === false) {
            deletedTableCells.push(previousLinkedTableCells[i]);
          }
        }
      } else {
        //previous deleted
        for (let i = 0; i < previousLinkedTableCells.length; i++) {
          deletedTableCells.push(previousLinkedTableCells[i]);
        }
      }
    } else {
      //all newly added
      if (changedLinkedTableCells.length > 0) {
        for (let i = 0; i < changedLinkedTableCells.length; i++) {
          addedTableCells.push({
            paraId: changedLinkedTableCells[i].paraId,
            startSentenceId: changedLinkedTableCells[i].startSentenceId,
            endSentenceId: changedLinkedTableCells[i].endSentenceId,
            startWordId: changedLinkedTableCells[i].startWordId,
            endWordId: changedLinkedTableCells[i].endWordId,
            rowId: changedLinkedTableCells[i].rowId,
            columnId: changedLinkedTableCells[i].columnId,
            phrase: changedLinkedTableCells[i].phrase,
            definedword: definitionTitleText,
          });
        }
      }
    }

    const editedTableCells: editedPhrases = {
      upsert: addedTableCells,
      deleted: deletedTableCells,
      bi: '',
    };
    return editedTableCells;
  };

  const mergePhrases = (
    firstEditedPhraseArray: editedPhrases,
    secondEditedPhraseArray: editedPhrases
  ) => {
    const upsertPhrases: phraseInfo[] = firstEditedPhraseArray.upsert.concat(
      secondEditedPhraseArray.upsert
    );
    const deletedPhrases: phraseInfo[] = firstEditedPhraseArray.deleted.concat(
      secondEditedPhraseArray.deleted
    );
    const biType = phraseBiMap[props.dataPointName];
    const mergedPhrases: editedPhrases = {
      upsert: upsertPhrases,
      deleted: deletedPhrases,
      bi: biType,
    };
    return mergedPhrases;
  };

  const getTempPhrase = () => {
    let tempPhraseRequest: LinkPhraseRequest = {
      mode: '',
      editedPhrases: { upsert: [], deleted: [], bi: '' },
    };

    const changedPhrasesArray =
      props.savedHighlightedPhrases !== null
        ? props.savedHighlightedPhrases
        : [];
    if (props.phraseInAddMode !== null) {
      changedPhrasesArray.push(props.phraseInAddMode);
    }
    let addedDeletedPhrases: editedPhrases = {
      upsert: [],
      deleted: [],
      bi: '',
    };

    const changedPhrases = filterPhrasesFromPhrasesArray(changedPhrasesArray);
    const changedTableCells =
      filterTableCellsFromPhraseArray(changedPhrasesArray);

    const editedPhrases = getAddedAndDeletedPhrases([], changedPhrases);
    const editedTableCells = getEditedTableCellPhrases([], changedTableCells);
    addedDeletedPhrases = mergePhrases(editedPhrases, editedTableCells);

    tempPhraseRequest = {
      editedPhrases: addedDeletedPhrases,
      mode: 'manual',
    };
    return tempPhraseRequest;
  };

  const onSave = () => {
    const tempPhraseRequest = getTempPhrase();
    const {
      clauseType,
      updatedClauseDataByType,
      sentenceData,
      clauseDataByType,
      postClauseDataByType,
      fileId,
    } = props;

    const updatedData = updatedClauseDataByType;
    let newData = updatedData;

    const addedData = tempPhraseRequest.editedPhrases.upsert;

    if (isPresent == 'Yes') {
      for (let i = 0; i < addedData.length; i++) {
        if (addedData[i].startSentenceId === addedData[i].endSentenceId) {
          newData = getClauseDataFormat(
            'add',
            clauseType as ClauseType,
            addedData[i],
            newData,
            sentenceData
          );
        }
      }
    } else {
      newData = getClauseObjects(clauseType, newData);
    }

    const diff = changesets.diff(clauseDataByType?.raw_content, newData, {
      children: '$index',
    });

    if (diff.length > -1) {
      postClauseDataByType(fileId, clauseType as ClauseType, diff, newData);
    }

    props.saveDeletePhrase(null, false);
    props.saveAddPhrase(null, false);
    props.savePhraseEditOption(false);
    props.saveHighlightedPhrases(null);
    props.editOptionSelected(false);
    props.saveHighlightedDataPoint(props.dataPointName);
    props.saveHighlightedTableCells(null);
    props.onClose();
  };

  return (
    <Box
      sx={{
        background: '#FFECF1',
        boxShadow: 'none',
        borderRadius: '15px',
        padding: '10px 16px',
      }}
    >
      <Typography fontWeight={600}>
        Add / Edit {props.dataPointName} Clause
      </Typography>
      <Stack direction="column" className="edit-clause-select" spacing={2}>
        <RadioButtonGroup
          row
          required
          name="isPresent"
          options={clauseOptions}
          valueKey="value"
          control={control}
        />
        {isPresent === 'Yes' && (
          <Stack width="100%" spacing={1}>
            <ControlledTextField
              name="definitionTitle"
              type="text"
              control={control}
              label="Enter Definition Title"
              value={definitionTitleText}
              onChange={handleChange}
              fullWidth
            />
            {linkPhraseOnDoc(props.phraseInAddMode)}
          </Stack>
        )}
        <Stack direction="row" width="100%">
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={onSave}
            disabled={
              props.savedHighlightedTableCells !== null &&
              props.savedHighlightedTableCells.length > 0
            }
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EditAddDefinition;
